import React, { FC, useEffect, useMemo, useState } from 'react';
import { Box, Button, IconButton, useMediaQuery } from '@mui/material';
import { Page, FullTable as Table, MobileTable } from '../../components';
import { IOccupierServiceAPI, getOccupierServiceProductTypes } from '../../fetch/occupier-services';
import { useToastContext } from '../../hooks';
import { IOccupierService, IOccupierServiceRes, IDropdownResponse } from '../../models';
import { Edit, DeleteOutlined } from '@mui/icons-material';
import { AddEditOccupierServiceModal } from './AddEditOccupierServiceModal';

export const OccupierServices: FC = () => {
  const { showToast } = useToastContext();
  const isMobile = useMediaQuery('(max-width: 960px)');
  const [loadingOccupierServices, setLoadingOccupierServices] = useState(false);
  const [occupierServices, setOccupierServices] = useState<IOccupierService[]>([]);
  const [page, setPage] = useState(0);
  const [perPage, setRowsPerPage] = useState(10);
  const [recordCount, setRecordCount] = useState(0);
  const [selectedSort, setSelectedSort] = useState<string>('');
  const [sortDirection, setSortDirection] = useState<{ [key: string]: 'Asc' | 'Desc' }>({});
  const [occupierServiceProductTypes, setOccupierServiceProductTypes] = useState<IDropdownResponse[]>([]);
  const [loadingOccupierServiceProductTypes, setLoadingOccupierServiceProductTypes] = useState(false);
  const [editingOccupierService, setEditingOccupierService] = useState<IOccupierService | undefined>(undefined);

  const fetchOccupierServiceProductTypes = async () => {
    setLoadingOccupierServiceProductTypes(true);
    try {
      const res = await getOccupierServiceProductTypes();
      setOccupierServiceProductTypes(res);
    } catch (error) {
      console.error(error);
      showToast('error', 'Error fetching product types.');
    } finally {
      setLoadingOccupierServiceProductTypes(false);
    }
  };

  const fetchOccupierServices = async () => {
    setLoadingOccupierServices(true);
    try {
      const sortParams = selectedSort
        ? { sortBy: selectedSort, sortDirection: sortDirection[selectedSort] }
        : {};
      const occupierServicesRes: IOccupierServiceRes = await IOccupierServiceAPI.getAll({
        page: page + 1,
        perPage,
        ...sortParams,
      });
      setOccupierServices(occupierServicesRes.records);
      setRecordCount(occupierServicesRes.totalRecordCount);
    } catch (error: any) {
      showToast('error', error?.detail || 'Error retrieving occupier services.');
    } finally {
      setLoadingOccupierServices(false);
    }
  };

  useEffect(() => {
    fetchOccupierServices();
    fetchOccupierServiceProductTypes();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    fetchOccupierServices();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, perPage, selectedSort, sortDirection]);

  const handleClickColumn = (column: string) => {
    // If no sort exists yet for this column, default to Asc.
    const currentDirection = sortDirection[column] || 'Asc';
    setSelectedSort(column);
    setSortDirection({
      ...sortDirection,
      [column]: currentDirection === 'Asc' ? 'Desc' : 'Asc',
    });
  };

  // Define table columns
  const columns = useMemo(() => {
    return [
      {
        accessor: 'productType',
        Header: 'Product Type',
        isServerSorted: selectedSort === 'productType',
        isServerSortedDesc: sortDirection.productType === 'Desc',
        handleClickColumn: () => handleClickColumn('productType'),
      },
      {
        accessor: 'category',
        Header: 'Category',
        sort: false,
      },
      {
        accessor: 'actions',
        Header: '',
        sort: false,
        Cell: ({
          cell: {
            row: { original },
          },
        }: any) => (
          <>
            <IconButton onClick={() => setEditingOccupierService(original)}>
              <Edit />
            </IconButton>
            <IconButton onClick={() => handleDeleteOccupierService(original)} color="error">
              <DeleteOutlined />
            </IconButton>
          </>
        ),
      },
    ];
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [occupierServiceProductTypes, selectedSort, sortDirection]);

    const handleDeleteOccupierService = async (occupierServiceToDelete: IOccupierService) => {

        const result = window.confirm('Are you sure you want to remove this occupier service?');
        if (!result) return;
        try {
      
          await IOccupierServiceAPI.delete(occupierServiceToDelete.occupierServicesId);
          showToast('success', 'Occupier Service deleted successfully.');
          fetchOccupierServices();
        } catch (error: any) {
          showToast('error', error?.detail || 'Error deleting occupier service.');
            }

  };

  const handleSaveOccupierService = async (updatedOccupierService: IOccupierService) => {
    try {
      await IOccupierServiceAPI.upsert(updatedOccupierService);
      showToast('success', updatedOccupierService.occupierServicesId ? 'Occupier Service updated.' : 'Occupier Service created.');
      fetchOccupierServices();
    } catch (error: any) {
      showToast('error', error?.detail || 'Error saving Occupier Service.');
    } finally {
      setEditingOccupierService(undefined);
    }
  };

  return (
    <Page title="Occupier Service Categories">
      <Box>
        <Box display="flex" justifyContent="flex-end" mb={0.25}>
          <Button
            variant="contained"
            color="secondary"
            onClick={() => setEditingOccupierService(IOccupierService.getDefault())}
          >
            Add Occupier Service Category
          </Button>
        </Box>
        <Table
          columns={columns}
          data={occupierServices}
          isLoading={loadingOccupierServices || loadingOccupierServiceProductTypes}
          stickyHeader
          serverPage={page}
          serverRecordCount={recordCount}
          serverPerPage={perPage}
          handlePage={setPage}
          handleRowsPerPage={setRowsPerPage}
          ResponsiveComponent={isMobile ? MobileTable : undefined}
          mobileProps={{
            fields: columns
              .filter(col => col.Header)
              .map(col => ({
                name: col.Header,
                accessor: col.accessor,
                Cell: col?.Cell,
              })),
              mobileAccordionLabel: (original: any) => original.productType,
            handleEdit: (original: any) => setEditingOccupierService(original),
          }}
        />
      </Box>
      <AddEditOccupierServiceModal
        open={!!editingOccupierService}
        occupierService={editingOccupierService}
        occupierServiceProductTypes={occupierServiceProductTypes}
        onClose={() => setEditingOccupierService(undefined)}
        onSave={handleSaveOccupierService}
      />
    </Page>
  );
};
